import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { io as socketIO } from 'socket.io-client';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) return { unreadSupportCount: 0, unreadEmailCount: 0, refresh: () => {} };
  return context;
};

const POLL_INTERVAL = 60000;
const LEADER_KEY = 'resunize_admin_poll_leader';
const LEADER_HEARTBEAT_MS = 15000;

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);
  const prevSupportRef = useRef(0);
  const prevEmailRef = useRef(0);
  const intervalRef = useRef(null);
  const socketRef = useRef(null);
  const tabIdRef = useRef(`tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const heartbeatRef = useRef(null);

  const canBecomeLeader = useCallback(() => {
    try {
      const raw = localStorage.getItem(LEADER_KEY);
      if (!raw) return true;
      const current = JSON.parse(raw);
      if (!current?.id || !current?.ts) return true;
      return Date.now() - current.ts > LEADER_HEARTBEAT_MS * 2;
    } catch {
      return true;
    }
  }, []);

  const isLeader = useCallback(() => {
    try {
      const raw = localStorage.getItem(LEADER_KEY);
      if (!raw) return false;
      const current = JSON.parse(raw);
      return current?.id === tabIdRef.current;
    } catch {
      return false;
    }
  }, []);

  const writeLeaderHeartbeat = useCallback(() => {
    localStorage.setItem(
      LEADER_KEY,
      JSON.stringify({ id: tabIdRef.current, ts: Date.now() })
    );
  }, []);

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  const showBrowserNotification = useCallback((title, body, tag) => {
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: tag || 'resunize-admin',
        renotify: true,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch {
      // Silent fail
    }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!user || user.role !== 'admin') return;
    if (document.visibilityState !== 'visible') return;
    if (!isLeader()) return;

    const results = await Promise.allSettled([
      api.get('/admin/support-chats/unread-count'),
      api.get('/admin/emails/stats'),
    ]);

    // Support chats
    if (results[0].status === 'fulfilled') {
      const count = results[0].value.data.count || 0;
      setUnreadSupportCount(count);
      if (count > prevSupportRef.current && prevSupportRef.current >= 0) {
        const diff = count - prevSupportRef.current;
        showBrowserNotification(
          'New Support Message',
          diff === 1 ? 'A user is waiting for support.' : `${diff} support chats need attention.`,
          'resunize-support'
        );
      }
      prevSupportRef.current = count;
    }

    // Emails
    if (results[1].status === 'fulfilled') {
      const count = parseInt(results[1].value.data.unread, 10) || 0;
      setUnreadEmailCount(count);
      if (count > prevEmailRef.current && prevEmailRef.current >= 0) {
        const diff = count - prevEmailRef.current;
        showBrowserNotification(
          'New Email',
          diff === 1 ? 'You have a new unread email.' : `${diff} new unread emails.`,
          'resunize-email'
        );
      }
      prevEmailRef.current = count;
    }
  }, [user, showBrowserNotification, isLeader]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      setUnreadSupportCount(0);
      setUnreadEmailCount(0);
      return;
    }
    requestPermission();
    prevSupportRef.current = -1;
    prevEmailRef.current = -1;
    if (canBecomeLeader()) {
      writeLeaderHeartbeat();
    }
    fetchAll();

    intervalRef.current = setInterval(fetchAll, POLL_INTERVAL);
    heartbeatRef.current = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (isLeader() || canBecomeLeader()) {
        writeLeaderHeartbeat();
      }
    }, LEADER_HEARTBEAT_MS);

    const token = localStorage.getItem('token');
    if (token) {
      const baseURL = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api$/, '');
      const s = socketIO(baseURL, { auth: { token }, transports: ['websocket', 'polling'] });
      socketRef.current = s;

      s.on('support:new-message', () => { fetchAll(); });
      s.on('support:admin-reply', () => { fetchAll(); });
      s.on('support:chat-closed', () => { fetchAll(); });
    }

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(heartbeatRef.current);
      if (isLeader()) {
        localStorage.removeItem(LEADER_KEY);
      }
      if (socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <NotificationContext.Provider value={{ unreadSupportCount, unreadEmailCount, refresh: fetchAll }}>
      {children}
    </NotificationContext.Provider>
  );
};
